import { Loader2, TrendingUp, AlertTriangle } from "lucide-react";
import { fmt, grandTotalFor, hasConvertibleRate } from "./oceanQuoteMath";

// Sticky footer showing live Buy/Sell totals and the margin earned, matching
// the mockup's bottom bar — recomputed straight from formik values on every
// render so it always reflects the current Buy/Sell charge tables.
const BottomSummaryBar = ({ formikData, loading, exchangeRates, isEdit, readOnly, onBack, onSubmitClick }) => {
  const { values } = formikData;
  const containers = values.containers || [];
  const buyTotal = grandTotalFor(values.buyCharges, containers, exchangeRates);
  const sellTotal = grandTotalFor(values.sellCharges, containers, exchangeRates);
  const earn = sellTotal - buyTotal;
  const earnPct = buyTotal ? (earn / buyTotal) * 100 : 0;

  // Same gap as BuySellChargesTable's warning — a currency used on a
  // non-zero row with no admin-configured exchange rate is silently treated
  // as 1:1 INR above, so these BUY/SELL/earn figures would be wrong too.
  const unconvertedCurrencies = [
    ...new Set(
      [...(values.buyCharges || []), ...(values.sellCharges || [])]
        .filter((r) => r.currency && r.currency !== "INR" && (r.amounts || []).some((a) => Number(a) > 0))
        .filter((r) => !hasConvertibleRate(r.currency, exchangeRates))
        .map((r) => r.currency)
    ),
  ];

  return (
    <div
      className="flex items-center justify-between flex-wrap gap-4 px-5 py-3.5 sm:px-8"
      style={{
        position: "sticky",
        bottom: 0,
        background: "#101828",
        color: "#fff",
        borderRadius: 12,
        boxShadow: "0 -6px 24px rgba(16,24,40,.18)",
        zIndex: 10,
      }}
    >
      <div className="flex items-center gap-6 sm:gap-8 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold tracking-wide text-gray-400">BUY RATE</div>
          <div className="text-lg font-extrabold mt-0.5">INR {fmt(buyTotal)}</div>
        </div>
        <div style={{ width: 1, height: 34, background: "#2A3346" }} />
        <div>
          <div className="text-[11px] font-semibold tracking-wide text-gray-400">SELL RATE</div>
          <div className="text-lg font-extrabold mt-0.5">INR {fmt(sellTotal)}</div>
        </div>
        <div
          className="flex items-center gap-2 rounded-xl px-3.5 py-2"
          style={{ background: "#182234", border: "1px solid #2A3346" }}
        >
          <TrendingUp size={15} style={{ color: earn >= 0 ? "#34D399" : "#f87171" }} />
          <span className="text-sm text-gray-300">
            You earn <b className="text-white">INR {fmt(earn)}</b>{" "}
            <b style={{ color: earn >= 0 ? "#34D399" : "#f87171" }}>{earnPct.toFixed(2)}%</b> on this rate
          </span>
        </div>
        {unconvertedCurrencies.length > 0 && (
          <div
            className="flex items-center gap-2 rounded-xl px-3.5 py-2"
            style={{ background: "#3a1f1f", border: "1px solid #7f1d1d" }}
            title={`No exchange rate configured for ${unconvertedCurrencies.join(", ")} — these figures use the raw amount as if it were INR. Add it under Admin → Exchange Rate.`}
          >
            <AlertTriangle size={15} style={{ color: "#f87171" }} />
            <span className="text-sm" style={{ color: "#fca5a5" }}>
              {unconvertedCurrencies.join(", ")} rate missing — totals not converted
            </span>
          </div>
        )}
      </div>
      {readOnly ? (
        <button
          type="button"
          onClick={onBack}
          className="font-semibold text-sm px-8 py-3 rounded-xl"
          style={{ background: "transparent", color: "#fff", border: "1px solid #2A3346" }}
        >
          Back to Queries
        </button>
      ) : (
        <button
          type="button"
          onClick={onSubmitClick}
          disabled={loading}
          className="flex items-center gap-2 font-bold text-sm px-8 py-3 rounded-xl transition-transform hover:scale-[1.03] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
          style={{ background: "#FCB614", color: "#1f2937", boxShadow: "0 3px 10px rgba(252,182,20,.4)" }}
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          {loading ? "Processing..." : isEdit ? "Update rates" : "Submit rates"}
        </button>
      )}
    </div>
  );
};

export default BottomSummaryBar;
